import DOMPurify from 'dompurify'
import {
  ContactNotesWithPagination,
  useContactNotesOffsetPagination,
} from 'modules/crm/pipeline/contact-card/note/hooks/use-notes-with-offset-pagination'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { ContactId } from '../../types/contact-interface'
import { PinIcon } from '@systemeio/ui/icons'

namespace ContactNotes {
  export interface Props {
    contactId: ContactId
  }
}

export function ContactNotes({ contactId }: ContactNotes.Props) {
  const { t } = useLocoTranslation()
  const { limit, pagination, setLimit, setPagination } = usePagination(true)

  const { data } = useContactNotesOffsetPagination({
    contactId: contactId,
    isFetchAllowed: !!contactId,
    limit,
    pagination,
  })

  const columns: ColumnDefinitionInterface<ContactNotesWithPagination>[] = [
    {
      key: 'content',
      widthPercent: 100,
      header: t('dashboard.contact_page.notes.note_content'),
      onRender: ({ content, isPinned }) => (
        <div className={'flex items-center justify-between gap-1.5'}>
          <p
            className={
              '[&>p>a]:text-brand-primary hyphens-auto whitespace-normal [&>p>strong]:font-bold'
            }
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
          />
          {isPinned && <PinIcon className={'fill-brand-primary h-[18px] w-[18px] shrink-0'} />}
        </div>
      ),
    },
  ]

  return (
    <ListingLayout
      counter={false}
      data={data}
      title={
        <h2 className={'h-[42px] text-[17px] font-bold'}>
          {t('dashboard.crm_pipeline.contact_modal.note')}
        </h2>
      }
      type="compact"
    >
      <Table
        columns={columns}
        data={data}
        paginator={{ pagination, setPagination }}
        limiter={{ limit, setLimit }}
        rowIdParser={row => row.paginationId}
      />
    </ListingLayout>
  )
}
